export const EMISOR_TRATAMIENTO_MEDICO = 'medico';
export const EMISOR_TRATAMIENTO_ADMIN = 'administrativo';

/** Badge dinámico: >1 receta = suscripto (verde); 1 receta = sin recordatorios. */
export function resolveRecordatoriosBadgeMeta({ cantidadRecetas }) {
  const cantidad = Math.max(0, Number(cantidadRecetas) || 0);

  if (cantidad > 1) {
    return {
      text: `✔ Suscripto a recordatorios · ${cantidad} recetas`,
      tone: 'on',
      hint: `Plan de ${cantidad} recetas: el paciente recibirá recordatorios del tratamiento. En el siguiente paso podés definir emisión y avisos por WhatsApp.`
    };
  }

  return {
    text: '✖ Sin recordatorios',
    tone: 'off',
    hint: 'Receta única sin recordatorios automáticos. En el siguiente paso elegí quién emite y si avisamos al paciente por WhatsApp.'
  };
}

export function coerceWebhookFlag(value, fallback = false) {
  if (value === undefined || value === null) return fallback;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  const raw = String(value).trim().toLowerCase();
  if (['1', 'true', 'si', 'sí', 'yes', 'on'].includes(raw)) return true;
  if (['0', 'false', 'no', 'off'].includes(raw)) return false;
  return fallback;
}

export function pedidoEsPami(pedido) {
  return coerceWebhookFlag(pedido?.es_pami, false);
}

export function resolveAprobacionWebhookDefaults(cantidadRecetas) {
  const cantidad = Math.max(1, Number(cantidadRecetas) || 1);
  const suscriptoRecordatorios = cantidad > 1;
  return {
    webhookAlta: suscriptoRecordatorios,
    webhookEmitir: !suscriptoRecordatorios
  };
}

export function resolveAprobacionFlujoDefaults(pedido, { isAdmin = false, linkedMedicoId = null } = {}) {
  const cantidad = Number(pedido?.cantidad_recetas) || 1;
  const linked = Number(linkedMedicoId);
  const medicoVinculado = Number.isFinite(linked) && linked > 0;
  const webhookDefaults = resolveAprobacionWebhookDefaults(cantidad);

  return {
    emisorTratamiento: (!isAdmin || medicoVinculado)
      ? EMISOR_TRATAMIENTO_MEDICO
      : EMISOR_TRATAMIENTO_ADMIN,
    ...webhookDefaults
  };
}

/** Sin médico previo de derivación (p. ej. alta manual): falta asignación al aprobar. */
export function pedidoRequiereSeleccionMedicoEnAprobacion(pedido) {
  const idNum = Number(pedido?.medico_id);
  if (Number.isFinite(idNum) && idNum > 0) return false;
  const nombre = String(pedido?.medico || pedido?.prestador_geclisa || '').trim();
  return !nombre;
}

/** Solo administradores pueden derivar a otro médico en la aprobación. */
export function puedeAdminDerivarMedicoEnAprobacion(pedido, isAdmin) {
  return Boolean(isAdmin) && pedidoRequiereSeleccionMedicoEnAprobacion(pedido);
}

export function resolveLinkedAdministrativoIdFromUser(user) {
  const id = Number(user?.administrativo_id);
  return Number.isFinite(id) && id > 0 ? id : null;
}

export function resolveAdministrativoEmisionEnAprobacion({
  administrativoSeleccionId = '',
  administrativos = [],
  linkedAdministrativoId = null,
  user = null
} = {}) {
  const sel = administrativos.find((a) => String(a.id) === String(administrativoSeleccionId));
  if (sel) {
    return {
      administrativoAsignadoId: String(sel.id),
      administrativoAsignadoNombre: sel.nombre || '',
      selectedAdministrativo: sel
    };
  }
  const linked = Number(linkedAdministrativoId);
  const linkedRow = Number.isFinite(linked) && linked > 0
    ? administrativos.find((a) => Number(a.id) === linked)
    : null;
  const fallbackName = String(user?.administrativo_nombre || '').trim();
  return {
    administrativoAsignadoId: linkedRow ? String(linkedRow.id) : (Number.isFinite(linked) && linked > 0 ? String(linked) : ''),
    administrativoAsignadoNombre: linkedRow?.nombre || fallbackName,
    selectedAdministrativo: linkedRow || null
  };
}

export function resolveMedicoTratanteEnAprobacion(pedido, {
  isAdmin = false,
  linkedMedicoId = null,
  medicoSeleccionId = '',
  medicos = [],
  user = null
} = {}) {
  if (puedeAdminDerivarMedicoEnAprobacion(pedido, isAdmin)) {
    const sel = medicos.find((m) => String(m.id) === String(medicoSeleccionId));
    return {
      medicoAsignadoId: sel ? String(sel.id) : '',
      medicoAsignadoNombre: sel?.nombre || '',
      selectedMed: sel || null
    };
  }

  if (pedidoRequiereSeleccionMedicoEnAprobacion(pedido)) {
    const linked = Number(linkedMedicoId);
    const sel = Number.isFinite(linked) && linked > 0
      ? medicos.find((m) => Number(m.id) === linked)
      : null;
    const fallbackName = String(user?.name || user?.username || '').trim();
    return {
      medicoAsignadoId: Number.isFinite(linked) && linked > 0 ? String(linked) : '',
      medicoAsignadoNombre: sel?.nombre || fallbackName,
      selectedMed: sel || (linked > 0 ? { id: linked, nombre: fallbackName } : null)
    };
  }

  const medicoAsignadoId = pedido?.medico_id ? String(pedido.medico_id) : '';
  const medicoAsignadoNombre = String(pedido?.medico || pedido?.prestador_geclisa || '').trim();
  const selectedMed = medicos.find((m) => String(m.id) === medicoAsignadoId)
    || medicos.find((m) => String(m.nombre || '').toLowerCase() === medicoAsignadoNombre.toLowerCase())
    || null;

  return { medicoAsignadoId, medicoAsignadoNombre, selectedMed };
}
